/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import type { ContextRouter } from 'react-router-dom';

import actions from '../../redux/actions';
import { getTrackSearchParams } from '../../redux/reducers/trackSearch';
import { getIsPlaying } from '../../redux/reducers/playerQueue';
import {
  getIsLoading,
  getSelectedCartItems,
  isAdding,
  isDeleting,
  isBuying,
} from '../../redux/reducers/cart';
import stringUtils from '../../utils/string';
import protonesUtils from '../../utils/protones';
import Sidebar from '../../components/Sidebar';
import ScrollableContent from '../../components/ScrollableContent';
import DataLoader from '../../components/DataLoader';
import Table from '../../components/Table';
import TrackListHeader from '../../components/TrackListHeader';
import TrackListRow from '../../components/TrackListRow';
import ActionButtons from '../../components/ActionButtons';
import PriceSummaryRow from '../../components/PriceSummaryRow';
import Button from '../../components/Button';
import styles from './styles.module.scss';

import type {
  Translate,
  SimplifiedMedia,
  MergedLicenseComponent,
  CartItem,
  ReduxState,
  LiferayError,
  WorklistItem,
  SearchParams,
  TrackSearchParams,
} from '../../types';

const mapStateToProps = (state: ReduxState, ownProps) => {
  return {
    currentTrackId: state.playerQueue.currentTrackId,
    isPlaying: getIsPlaying(state),
    loadingItems: state.cart.cartItemsBeingDeleted,
    cartItems: state.cart.cartItems,
    selectedCartItems: getSelectedCartItems(state),
    selectedComponentsByItem: state.cart.selectedComponentsByItem,
    isFetching: state.cart.isFetching,
    isLoading: getIsLoading(state),
    isAdding: isAdding(state),
    isDeleting: isDeleting(state),
    isBuying: isBuying(state),
    cartError: state.cart.error,
    trackSearchParams: getTrackSearchParams(state),
  };
};

type Props = ContextRouter & {
  cartItems: CartItem[],
  selectedCartItems: CartItem[],
  selectedComponentsByItem: {
    [cartItemId: $PropertyType<CartItem, 'id'>]: $PropertyType<MergedLicenseComponent, 'id'>[],
  },
  loadingItems: number[],
  currentTrackId: number,
  isPlaying: boolean,
  playTracks: typeof actions.playTracks,
  downloadTracks: typeof actions.downloadTracks,
  fetchCartItems: typeof actions.fetchCartItems,
  deleteCartItems: typeof actions.deleteCartItems,
  buyCartItems: typeof actions.buyCartItems,
  deselectAllCartItems: typeof actions.deselectAllCartItems,
  toggleSelectAllCartItems: typeof actions.toggleSelectAllCartItems,
  toggleSelectCartItem: typeof actions.toggleSelectCartItem,
  toggleSelectCartItemComponent: typeof actions.toggleSelectCartItemComponent,
  askQuotation: typeof actions.askQuotation,
  trackSearchParams: TrackSearchParams,
  displayMessage: typeof actions.displayMessage,
  isFetching: boolean,
  isLoading: boolean,
  isAdding: boolean,
  isDeleting: boolean,
  isBuying: boolean,
  cartError: ?LiferayError,
  t: Translate,
};

type State = {
  expandedItems: {
    [itemId: number]: boolean,
  },
};

class Cart extends React.Component<Props, State> {
  state = {
    expandedItems: {},
  };

  get expandedItemIds(): number[] {
    return _.map(_.keys(_.pickBy(this.state.expandedItems, v => v === true)), v => Number(v));
  }

  componentDidMount() {
    this.props.fetchCartItems();
  }

  _toggleExpandSelectedItems = () => {
    const { cartItems, selectedComponentsByItem } = this.props;
    const { expandedItems } = this.state;
    const selectedItems = cartItems.filter(item =>
      selectedComponentsByItem.hasOwnProperty(item.id)
    );
    const selectedItemIds = selectedItems.map(i => i.id);
    const isAllExpanded = selectedItemIds.every(id => this.expandedItemIds.includes(id));
    for (const itemId of selectedItemIds) {
      expandedItems[itemId] = !isAllExpanded;
    }
    this.setState({ expandedItems });
    this.forceUpdate();
  };

  _buySelectedItems = async () => {
    const { buyCartItems, fetchCartItems, selectedCartItems, t } = this.props;
    //eslint-disable-next-line
    if (confirm(t('DO_YOU_CONFIRM_PURCHASE'))) {
      const boughtItemsIds = (await buyCartItems(selectedCartItems)) || [];
      await fetchCartItems();
      protonesUtils.goTo(this.props, protonesUtils.routes.purchases.find());
      return boughtItemsIds;
    }
    return [];
  };

  _handleMediaClick = (media: SimplifiedMedia) => {
    protonesUtils.goTo(this.props, protonesUtils.routes.medias.get(media.id));
  };

  _askQuotation = (worklistItems: WorklistItem[]) => {
    this.props.askQuotation(worklistItems);
    protonesUtils.goTo(this.props, protonesUtils.routes.ondemand.find());
  };

  _handleRefreshCart = () => {
    this.props.fetchCartItems();
  };

  _handleTrackSearch = (params: SearchParams) => {
    const { performTrackSearch, trackSearchParams } = this.props;
    performTrackSearch(_.merge({}, trackSearchParams, params));
    protonesUtils.goTo(this.props, protonesUtils.routes.tracks.find());
  };

  _renderActionButtons = (items: CartItem[]) => {
    const { deleteCartItems } = this.props;
    const worklistItems = items.map(item => item.worklistItem);
    const unquotedWorklistItems = worklistItems.filter(
      protonesUtils.getCanAskQuotationForWorklistItem
    );
    const canAskQuotation = unquotedWorklistItems.length > 0;
    return (
      <ActionButtons
        worklist={
          items.length === 1
            ? {
                fetching: false,
                data: () => {
                  const item = items[0];
                  const worklistId = item.worklistItem.worklistId;
                  protonesUtils.goTo(this.props, protonesUtils.routes.worklists.get(worklistId));
                },
              }
            : undefined
        }
        delete={{ fetching: false, data: () => deleteCartItems(items) }}
        ask={
          canAskQuotation
            ? {
                fetching: false,
                data: () => this._askQuotation(unquotedWorklistItems),
              }
            : undefined
        }
      />
    );
  };

  _renderData = (cartItems: CartItem[]) => {
    const {
      loadingItems,
      currentTrackId,
      isPlaying,
      playTracks,
      isBuying,
      selectedComponentsByItem,
      t,
    } = this.props;
    const numOfCartItems = cartItems.length;
    const selectedItems = cartItems.filter(item =>
      selectedComponentsByItem.hasOwnProperty(item.id)
    );
    const isSelectedAll = cartItems
      ? selectedItems.length === cartItems.length && cartItems.length > 0
      : false;
    const isSelectedSome = !isSelectedAll && selectedItems.length > 0;
    const isSelectedAllExpanded = selectedItems.every(item =>
      this.expandedItemIds.includes(item.id)
    );

    const selectedTracks = selectedItems.map(item => item.worklistItem.track);
    if (numOfCartItems === 0) {
      return (
        <div className={styles.root}>
          <ScrollableContent>
            <div className={styles.container}>
              <div className={styles.centeredContent}>
                <h3 className={styles.title}>{t('EMPTY_CART')}</h3>
              </div>
            </div>
          </ScrollableContent>
        </div>
      );
    }
    const itemsTotalPrice = cartItems.reduce((acc, item) => {
      const itemComponents = item.worklistItem.mergedComponents || [];
      const itemPrice = protonesUtils.getLicenseComponentsTotalPrice(itemComponents);
      return acc + itemPrice;
    }, 0);

    const selectedItemsTotalPrice = selectedItems.reduce((acc, item) => {
      const itemComponents = item.worklistItem.mergedComponents || [];
      const itemSelectedComponents = itemComponents.filter(component =>
        (selectedComponentsByItem[item.id] || []).includes(component.id)
      );
      const itemPrice = protonesUtils.getLicenseComponentsTotalPrice(itemSelectedComponents);
      return acc + itemPrice;
    }, 0);

    const selectedItemsDiscountedPrice = selectedItems.reduce((acc, item) => {
      const itemComponents = item.worklistItem.mergedComponents || [];
      const itemSelectedComponents = itemComponents.filter(component =>
        (selectedComponentsByItem[item.id] || []).includes(component.id)
      );
      const itemDiscountedPrice = protonesUtils.getLicenseComponentsTotalDiscountedPrice(
        itemSelectedComponents
      );
      return acc + itemDiscountedPrice;
    }, 0);
    return (
      <div className={styles.container}>
        <h2>{t('CART')}</h2>
        <h3>
          {`${numOfCartItems} ${
            numOfCartItems > 1
              ? t('TRACKS').toLowerCase()
              : `${t('TRACK').toLowerCase()} ${t('IN_CART').toLowerCase()}`
          }`}
        </h3>
        <Table.Table>
          <TrackListHeader
            isSelectable={true}
            showCheckbox={true}
            isSelected={isSelectedAll}
            isSelectedIndeterminate={isSelectedSome}
            onCheckboxClick={this.props.toggleSelectAllCartItems}
            isPlayerVisible={selectedTracks.length > 0}
            showSentToCartDate={true}
            onPlayClick={() => playTracks(selectedTracks)}
            onDetailsExpand={this._toggleExpandSelectedItems}
            isDetailsExpanded={isSelectedAllExpanded}
            showActionButtons={Object.keys(selectedComponentsByItem).length > 0}
            actionButtons={this._renderActionButtons(selectedItems)}
            hasUsage={true}
          />
          {cartItems.map((item, index) => {
            const selectedComponentIds = selectedComponentsByItem[item.id] || [];
            const isLoading: boolean = _.includes(loadingItems, item.id);
            const components = item.worklistItem.mergedComponents || [];
            const hasAllComponentsSelected =
              components.length > 0 && components.every(c => selectedComponentIds.includes(c.id));
            const hasSomeComponentsSelected =
              !hasAllComponentsSelected &&
              components.some(c => selectedComponentIds.includes(c.id));
            const repricedTrack = item.worklistItem.track;
            repricedTrack.price = item.price;
            const isExpanded = !!this.state.expandedItems[item.id];
            return (
              <TrackListRow
                key={`${index}-${item.id}`}
                track={repricedTrack}
                medias={repricedTrack.medias}
                usage={item.worklistItem.usage}
                isReadOnlyUsage={true}
                isSelectable={true}
                isSelected={hasAllComponentsSelected}
                isSelectedIndeterminate={hasSomeComponentsSelected}
                isCurrentTrack={currentTrackId === item.worklistItem.track.id}
                isPlaying={isPlaying}
                isLoading={isLoading}
                isDetailsExpanded={isExpanded}
                showSentToCartDate={true}
                sentToCartDate={item.modifiedDate}
                onMediaClick={this._handleMediaClick}
                onPlayClick={() => playTracks([item.worklistItem.track])}
                onPerformerClick={person =>
                  this._handleTrackSearch({
                    text: person.publicName,
                    flags: { people: true },
                  })
                }
                onAuthorClick={person =>
                  this._handleTrackSearch({
                    text: person.publicName,
                    flags: { people: true },
                  })
                }
                onCheckboxClick={() => this.props.toggleSelectCartItem(item.id)}
                onComponentCheckboxClick={componentId =>
                  this.props.toggleSelectCartItemComponent(item.id, componentId)
                }
                onComponentHeaderCheckboxClick={() => this.props.toggleSelectCartItem(item.id)}
                components={components}
                selectedComponents={selectedComponentIds}
                onAskQuotationForComponents={mergedComponents =>
                  this._askQuotation([{ ...item.worklistItem, mergedComponents: mergedComponents }])
                }
                defaultTabId={'LICENSE_COMPONENTS'}
                actionButtons={this._renderActionButtons([item])}
              />
            );
          })}
        </Table.Table>
        <PriceSummaryRow
          description={t('TOTAL_PRICE_ELEMENTS')}
          value={stringUtils.prettifyMoney(itemsTotalPrice)}
          isFirstRow={true}
        />
        <PriceSummaryRow
          description={t('DISCOUNTS_OR_INCREASES')}
          value={stringUtils.prettifyMoney(selectedItemsTotalPrice - selectedItemsDiscountedPrice)}
        />
        <PriceSummaryRow
          description={t('NET_PRICE_ELEMENTS')}
          value={stringUtils.prettifyMoney(selectedItemsDiscountedPrice)}
          isPrimary={true}
        />
        <br />
        <br />
        <div className={styles.buyButton}>
          <Button
            disabled={Object.keys(selectedComponentsByItem).length === 0}
            loading={isBuying}
            text={t('BUY')}
            appearance={'primary'}
            onClick={this._buySelectedItems}
          />
        </div>
      </div>
    );
  };

  render() {
    const { isFetching, cartError, cartItems, t } = this.props;
    return (
      <div className={styles.root}>
        <Sidebar title={t('CART')} onRefresh={this._handleRefreshCart} loading={isFetching} />
        <ScrollableContent>
          <DataLoader
            loading={isFetching}
            loadingIsBlocking={true}
            error={cartError}
            data={cartItems}
            render={this._renderData}
          />
        </ScrollableContent>
      </div>
    );
  }
}

export default withTranslation()(connect(mapStateToProps, actions)(Cart));
